/**
 * Numbered pages for a list the server cuts: `‹ Prev  1 2 3 … 104  Next ›`.
 *
 * Standalone rather than built into DataTable on purpose. DataTable sorts and
 * searches the rows it is handed, which is only honest while it holds the
 * whole list; a paged list holds one page, so the table above a Pager has its
 * client sorting switched off, and every unpaged table keeps working as it
 * did.
 *
 * The window is always the first page, the last, and one either side of the
 * current one, with "…" for what is skipped — the control stays the same width
 * on page 1 and page 104. A gap that would hide exactly one page shows that
 * page instead: the ellipsis costs the same space and says less.
 *
 * Every number comes from `total`, never from the rows on screen: the range
 * line describes the whole result ("101–150 of 5,187"), which the length of
 * one page cannot.
 *
 * Prev/Next are `aria-disabled` rather than `disabled` at the ends. A focused
 * button that becomes `disabled` drops focus to <body>, so a keyboard user who
 * pages to the end with Next would be thrown to the top of the document.
 */

import { formatNumber } from '../../lib/format';
import { Button } from './Button';
import { Select, SelectItem } from './Form';

import '../../styles/pager.css';

export const DEFAULT_PAGE_SIZES: readonly number[] = [25, 50, 100, 200];

export interface PagerProps {
  /** 1-based. A page past the end is drawn as the last page; clamping the
   *  caller's own state (e.g. the URL) is the caller's job. */
  page: number;
  pageSize: number;
  /** Rows in the whole result, not on this page. */
  total: number;
  onPageChange: (page: number) => void;
  /** Omit to hide the rows-per-page select. */
  onPageSizeChange?: (size: number) => void;
  pageSizes?: readonly number[];
  /** The landmark's name. Two pagers on one screen need different ones. */
  label?: string;
}

type PageItem = number | 'gap-before' | 'gap-after';

/** First, last, current ±1, and a marker for each run that was skipped. */
export function pageItems(page: number, lastPage: number): PageItem[] {
  const wanted = new Set<number>([1, lastPage]);
  for (let p = page - 1; p <= page + 1; p += 1) {
    if (p >= 1 && p <= lastPage) wanted.add(p);
  }
  const pages = Array.from(wanted).sort((a, b) => a - b);

  const items: PageItem[] = [];
  let previous = 0;
  for (const p of pages) {
    if (p - previous === 2) {
      items.push(p - 1);
    } else if (p - previous > 2) {
      items.push(previous === 1 ? 'gap-before' : 'gap-after');
    }
    items.push(p);
    previous = p;
  }
  return items;
}

export function Pager({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizes = DEFAULT_PAGE_SIZES,
  label = 'Pagination',
}: PagerProps) {
  const size = Math.max(1, pageSize);
  const lastPage = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(1, page), lastPage);

  const from = total === 0 ? 0 : (current - 1) * size + 1;
  const to = Math.min(current * size, total);
  const range =
    total === 0
      ? '0 of 0'
      : `${formatNumber(from)}–${formatNumber(to)} of ${formatNumber(total)}`;

  // A size the caller allows but this list does not offer is still selected,
  // rather than the select silently showing a different number than the table.
  const sizes = pageSizes.includes(pageSize)
    ? pageSizes
    : [...pageSizes, pageSize].sort((a, b) => a - b);

  const go = (target: number) => {
    if (target < 1 || target > lastPage || target === current) return;
    onPageChange(target);
  };

  const atStart = current <= 1;
  const atEnd = current >= lastPage;

  return (
    <nav className="pager" aria-label={label}>
      {/* Polite, so a keyboard user paging with Next hears where they landed
          without focus having to move off the button. */}
      <p className="pager__range" aria-live="polite">
        {range}
      </p>

      {lastPage > 1 ? (
        <ul className="pager__pages">
          <li>
            <Button
              kind="ghost"
              size="sm"
              className="pager__step"
              aria-disabled={atStart || undefined}
              title="Previous page"
              onClick={() => go(current - 1)}
            >
              <span aria-hidden="true">‹</span> Prev
            </Button>
          </li>
          {pageItems(current, lastPage).map((item) =>
            typeof item === 'number' ? (
              // Keyed by the page number, so the button you pressed survives
              // the window moving and keeps keyboard focus.
              <li key={item}>
                <Button
                  kind="ghost"
                  size="sm"
                  className="pager__page"
                  aria-label={`Page ${formatNumber(item)}`}
                  aria-current={item === current ? 'page' : undefined}
                  onClick={() => go(item)}
                >
                  {formatNumber(item)}
                </Button>
              </li>
            ) : (
              <li key={item} className="pager__gap" aria-hidden="true">
                …
              </li>
            ),
          )}
          <li>
            <Button
              kind="ghost"
              size="sm"
              className="pager__step"
              aria-disabled={atEnd || undefined}
              title="Next page"
              onClick={() => go(current + 1)}
            >
              Next <span aria-hidden="true">›</span>
            </Button>
          </li>
        </ul>
      ) : null}

      {onPageSizeChange ? (
        <Select
          labelText="Rows per page"
          className="pager__size"
          value={String(pageSize)}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
        >
          {sizes.map((option) => (
            <SelectItem key={option} value={option} text={String(option)} />
          ))}
        </Select>
      ) : null}
    </nav>
  );
}
